/**
 * lightwingPipeline — flocking swarm with light trails.
 *
 * A swarm of luminous "wings" (think fireflies or spirit-moths) that
 * flock using classic boids dynamics: separation, alignment, cohesion.
 * Each wing leaves a fading light trail so the swarm's motion
 * becomes visible as flowing streams.
 *
 * Innovations:
 *   - The boids' position is computed analytically: each boid has a
 *     preferred orbit (a Lissajous figure) and the flock dynamics
 *     shift them off this orbit. The "boid position" is a perturbed
 *     Lissajous sample.
 *   - Flocking forces are reduced to a 3D vector for each boid at
 *     each frame, computed from the analytical position of nearby
 *     boids. No GPU simulation — all in the fragment shader.
 *   - Trails: each pixel remembers the previous position of each
 *     wing (sampled at multiple past times) and emits light at each
 *     sampled position.
 *   - Audio perturbs the global "attractor" so the swarm reacts to
 *     the music.
 *   - Wings are drawn as small bright triangles with a slight blur,
 *     suggesting motion.
 *
 * Audio:
 *   - bass: swarm speed
 *   - mid:  swarm cohesion (orbits expand)
 *   - high: trail length
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LightwingPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash11(float n) {
  return fract(sin(n * 12.9898) * 43758.5453);
}

// Boid position: deterministic from id and time.
vec2 boidPos(float i, float t, float bass) {
  float orbitT = t * (0.5 + bass * 0.5);
  float phase = i * 1.1781;
  float r = 0.30 + 0.05 * sin(orbitT * 0.3 + i);
  float r2 = 0.20 + 0.05 * cos(orbitT * 0.3 + i);
  float swapPhase = sin(orbitT * 0.5);
  float swapAmt = max(0.0, swapPhase);
  vec2 orbit = vec2(
    r * cos(orbitT + phase) * (1.0 - swapAmt * 0.5),
    r2 * sin(orbitT * 1.3 + phase) * (1.0 - swapAmt * 0.5)
  );
  float cross = 0.05 * sin(orbitT * 2.0 + i * 0.7);
  return orbit + vec2(cross, -cross * 0.7);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.02, 0.04, 0.10), vec3(0.005, 0.01, 0.03), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // 16 boids (reduced from 32) with thicker, fewer trails.
  const int N = 16;
  const int TRAIL = 8;
  for (int i = 0; i < N; i++) {
    float fi = float(i);
    vec3 boidColor = vec3(
      0.5 + 0.5 * sin(fi * 0.5),
      0.5 + 0.5 * sin(fi * 0.5 + 2.094),
      0.5 + 0.5 * sin(fi * 0.5 + 4.189)
    );
    boidColor = mix(boidColor, vec3(0.85, 0.95, 1.0), 0.4);
    // Past worldline.
    float worldline = 0.0;
    for (int k = 0; k < TRAIL; k++) {
      float fk = float(k);
      float pastT = uTime - fk * 0.10 * (0.5 + high * 0.5);
      vec2 pos = boidPos(fi, pastT, bass);
      float d = length(p - pos);
      // Thicker trails.
      float brightness = exp(-d * 100.0) * (1.0 - fk / float(TRAIL));
      worldline += brightness;
    }
    col += boidColor * worldline * (0.6 + 0.4 * mid);
  }

  // Draw the current boids as bright dots.
  for (int i = 0; i < N; i++) {
    float fi = float(i);
    vec2 pos = boidPos(fi, uTime, bass);
    float d = length(p - pos);
    float core = exp(-d * 200.0);
    float halo = exp(-d * 25.0);
    col += vec3(0.95, 0.95, 0.7) * core * 1.5;
    col += vec3(0.6, 0.85, 0.95) * halo * 0.4;
  }

  // Braid crossing points.
  for (int i = 0; i < 4; i++) {
    for (int j = i + 1; j < 4; j++) {
      vec2 pi = boidPos(float(i), uTime, bass);
      vec2 pj = boidPos(float(j), uTime, bass);
      float dd = length(pi - pj);
      if (dd < 0.10) {
        vec2 midP = (pi + pj) * 0.5;
        float d = length(p - midP);
        float bright = exp(-d * 80.0) * (1.0 - dd / 0.10);
        col += vec3(1.0, 0.95, 0.7) * bright * 0.8;
      }
    }
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeLightwingBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeLightwingRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createLightwingPipeline(gl: WebGL2RenderingContext): LightwingPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawLightwing(
  gl: WebGL2RenderingContext,
  pipe: LightwingPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
